import { useState } from "react";
import { Box, Typography, useTheme, TextField, Button, Grid, Card, CardContent, Alert, Paper } from "@mui/material";
import { tokens } from "../../theme";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import Header from "../../components/Header";

const RegisterModel = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    authorFirstName: "",
    authorLastName: "",
    code: "# Python code here\n\ndef create_model():\n    # Your model code\n    pass",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setIsSubmitted(true);
    setSubmitted({
      id: "CNN",
      author: "sgh s",
      created: "6/1/2025, 4:28:09 AM",
    });
  };

  const handleClearForm = () => {
    setFormData({
      name: "",
      description: "",
      authorFirstName: "",
      authorLastName: "",
      code: "# Python code here\n\ndef create_model():\n    # Your model code\n    pass",
    });
  };

  const sectionLabel = {
    color: colors.grey[500],
    fontSize: "0.6875rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    mb: 1.25,
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: "auto" }}>
      <Header
        icon={<ModelTrainingIcon />}
        title="Register Model"
        subtitle="Register a new model architecture in Python."
      />

      {isSubmitted && (
        <Alert
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          severity="success"
          sx={{ mb: 2 }}
        >
          Model submitted successfully.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography sx={sectionLabel}>Model details</Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField fullWidth required size="small" label="Model name" name="name" value={formData.name} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth required size="small" multiline rows={3} label="Description" name="description" value={formData.description} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth required size="small" label="Author first name" name="authorFirstName" value={formData.authorFirstName} onChange={handleChange} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth required size="small" label="Author last name" name="authorLastName" value={formData.authorLastName} onChange={handleChange} />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ mt: 0.5 }}>
                      <Typography sx={sectionLabel}>Model code (Python)</Typography>
                      <Box
                        sx={{
                          backgroundColor: "#0f172a",
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.6, borderBottom: "1px solid rgba(255,255,255,0.08)", backgroundColor: "rgba(255,255,255,0.03)" }}>
                          {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                            <Box key={c} sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: c, opacity: 0.85 }} />
                          ))}
                          <Typography sx={{ ml: 1, color: "rgba(255,255,255,0.45)", fontFamily: "monospace", fontSize: "0.7rem" }}>
                            model.py
                          </Typography>
                        </Box>
                        <TextField
                          multiline
                          fullWidth
                          rows={14}
                          name="code"
                          value={formData.code}
                          onChange={handleChange}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              border: "none",
                              borderRadius: 0,
                              backgroundColor: "#0f172a",
                              "& fieldset": { border: "none" },
                            },
                            "& .MuiInputBase-input": {
                              color: "var(--fx-border)",
                              fontFamily: "monospace",
                              fontSize: "0.8125rem",
                              p: 1.5,
                              backgroundColor: "#0f172a",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
                      <Button type="submit" variant="contained" color="primary" size="small">
                        Submit
                      </Button>
                      <Button variant="outlined" color="primary" size="small" onClick={handleClearForm}>
                        Clear form
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {submitted ? (
            <Card>
              <CardContent sx={{ p: 2.5 }}>
                <Typography sx={sectionLabel}>Submitted</Typography>
                <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: "1rem", mb: 1.5 }}>
                  {submitted.id}
                </Typography>
                {[
                  ["Author", submitted.author],
                  ["Created", submitted.created],
                ].map(([k, v]) => (
                  <Box key={k} sx={{ display: "flex", justifyContent: "space-between", py: 0.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography sx={{ color: colors.grey[500], fontSize: "0.75rem" }}>{k}</Typography>
                    <Typography sx={{ color: 'var(--fx-text-primary)', fontSize: "0.8125rem", fontWeight: 500 }}>{v}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          ) : (
            <Paper variant="outlined" sx={{ p: 2.5, borderStyle: "dashed", borderColor: theme.palette.divider, textAlign: "center" }}>
              <Typography sx={{ color: colors.grey[500], fontSize: "0.8125rem" }}>
                Submitted models will appear here.
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default RegisterModel;
